import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalBody, ModalFooter, useToast } from '../sectepe-ui/index';
import companyService, { Akte, AktenEintrag, CompanyContact, EintragArt, KontaktRolle } from '../services/companyService';
import crmService from '../services/crmService';
import quoteService, { Quote, STATUS_LABEL as ANGEBOT_STATUS, euro as euroCents } from '../services/quoteService';
import taskService, { Task } from '../services/taskService';
import auftragService, { AUFTRAG_STATUS_LABEL } from '../services/auftragService';
import rechnungService, { RECHNUNG_STATUS_LABEL } from '../services/rechnungService';
import abrechnungService, { POSTEN_STATUS_LABEL, QUELLE_LABEL } from '../services/abrechnungService';
import projektService, { PROJEKT_STATUS_LABEL } from '../services/projektService';
import vertragService, { Vertrag, VERTRAG_ART_LABEL, VERTRAG_STATUS_LABEL, VERTRAG_STATUS_TONE } from '../services/vertragService';
import { VertragFristBadge } from '../vertrieb/VertragFristBadge';
import type { SalesOrder, Invoice, Project, Abrechnungsposten, OffenePostenFirma } from '../services/erpTypes';
import { PageHeader, EmptyState, LoadingState, StatusBadge, useConfirm } from '../components/ui';
import { Icon } from '../components/icons';
import { SalesProcessBar } from '../vertrieb/SalesProcessBar';
import { KpiRow } from '../vertrieb/KpiRow';
import { AccountProfil } from '../vertrieb/AccountProfil';
import { euro, zeit, tagLang, faelligkeit, fehlerText } from '../vertrieb/format';
import { FIRMEN_STATUS } from './CompaniesPage';
import { RechteHinweis } from '../vertrieb/RechteHinweis';
import { useKampagnenZuordnung, KampagneVerweis } from '../vertrieb/kampagnen';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';

/**
 * Die Firmenakte: alles zu einem Haus auf einer Seite.
 *
 * Die Chronik ist der Kern. Vor einem Anruf ist die Frage nie "welche Felder
 * sind gepflegt", sondern "was ist mit denen bisher gelaufen". Neu: von hier
 * aus entstehen Vorgang, Angebot und Aufgabe -- vorher musste man dafuer die
 * Seite wechseln und die Firma erneut eintippen.
 *
 * Welle 2: Schreiben verlangt vertrieb.bearbeiten (Kontakt loesen
 * vertrieb.loeschen, Angebot anlegen angebot.bearbeiten); ERP-Bloecke laden
 * nur mit ihrem Leserecht, damit niemand 403 sammelt (Entscheidung 17):
 * Auftraege erp.auftrag.lesen, Rechnungen und Abrechnungsposten
 * erp.rechnung.lesen, offene Posten erp.offene_posten.lesen -- nur als Summe
 * vom Server, der Vertrieb sieht keine einzelne Rechnung.
 */

const ART_LABEL: Record<EintragArt, string> = {
  aktivitaet: 'Reaktion',
  notiz: 'Notiz',
  vorgang: 'Vorgang',
  kontakt: 'Kontakt',
};

const DEAL_STATUS: Record<string, { label: string; tone: 'success' | 'danger' | 'info' }> = {
  open: { label: 'offen', tone: 'info' },
  won: { label: 'gewonnen', tone: 'success' },
  lost: { label: 'verloren', tone: 'danger' },
};

/** Chronik nach Tagen gruppieren -- eine endlose Liste liest niemand. */
const nachTagen = (eintraege: AktenEintrag[]) => {
  const gruppen: { tag: string; eintraege: AktenEintrag[] }[] = [];
  for (const e of eintraege) {
    const schluessel = e.zeitpunkt.slice(0, 10);
    const letzte = gruppen[gruppen.length - 1];
    if (letzte && letzte.tag === schluessel) letzte.eintraege.push(e);
    else gruppen.push({ tag: schluessel, eintraege: [e] });
  }
  return gruppen;
};

/** Rollen beim Kunden (kontaktRollen, company.service.ts); Empfaenger der Sicherheitsinformationen aus Core. */
const KONTAKT_ROLLEN: Array<{ value: KontaktRolle; label: string }> = [
  { value: 'sicherheit', label: 'Sicherheit' },
  { value: 'isb', label: 'ISB' },
  { value: 'dsb', label: 'DSB' },
  { value: 'gf', label: 'GF' },
  { value: 'technik', label: 'Technik' },
  { value: 'abrechnung', label: 'Abrechnung' },
];

const LEER_KONTAKT = { email: '', firstName: '', lastName: '', jobTitle: '', phone: '', linkedInUrl: '', isPrimary: false };

const CompanyAktePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { push } = useToast();
  const { confirm, prompt } = useConfirm();
  const zuordnung = useKampagnenZuordnung();
  const { darf } = useBerechtigung();
  const darfBearbeiten = darf('vertrieb.bearbeiten');
  const darfLoesen = darf('vertrieb.loeschen');
  const darfAngebot = darf('angebot.bearbeiten');
  const siehtAngebote = darf('angebot.lesen');
  const siehtAuftraege = darf('erp.auftrag.lesen');
  const siehtRechnungen = darf('erp.rechnung.lesen');
  const siehtOffenePosten = darf('erp.offene_posten.lesen');
  const darfZahlungen = darf('erp.zahlung.lesen');
  const siehtProjekte = darf('projekt.lesen');

  const [akte, setAkte] = useState<Akte | null>(null);
  const [angebote, setAngebote] = useState<Quote[]>([]);
  const [aufgaben, setAufgaben] = useState<Task[]>([]);
  const [auftraege, setAuftraege] = useState<SalesOrder[]>([]);
  const [rechnungen, setRechnungen] = useState<Invoice[]>([]);
  const [abrechnungsposten, setAbrechnungsposten] = useState<Abrechnungsposten[]>([]);
  const [offenePosten, setOffenePosten] = useState<OffenePostenFirma | null>(null);
  const [projekte, setProjekte] = useState<Project[]>([]);
  const [vertraege, setVertraege] = useState<Vertrag[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [notiz, setNotiz] = useState('');
  const [notizArt, setNotizArt] = useState('note');
  const [filter, setFilter] = useState<EintragArt | ''>('');

  const [neuKontakt, setNeuKontakt] = useState(LEER_KONTAKT);
  const [zeigeKontaktform, setZeigeKontaktform] = useState(false);

  const laden = useCallback(async () => {
    if (!id) return;
    try {
      // ERP-Listen (ADR 0008) sind Beiwerk: fehlen sie, bleibt die Akte lesbar.
      // Ohne Leserecht wird gar nicht erst gefragt (Welle 2 antwortet sonst 403).
      const [a, q, t, ao, re, pr, ap, op, vt] = await Promise.all([
        companyService.getAkte(id),
        siehtAngebote ? Promise.resolve().then(() => quoteService.list({ companyId: id })).catch(() => [] as Quote[]) : [] as Quote[],
        Promise.resolve().then(() => taskService.list({ companyId: id, status: 'open' })).catch(() => [] as Task[]),
        siehtAuftraege ? Promise.resolve().then(() => auftragService.list({ companyId: id })).catch(() => [] as SalesOrder[]) : [] as SalesOrder[],
        siehtRechnungen ? Promise.resolve().then(() => rechnungService.list({ companyId: id })).catch(() => [] as Invoice[]) : [] as Invoice[],
        siehtProjekte ? Promise.resolve().then(() => projektService.list({ companyId: id })).catch(() => [] as Project[]) : [] as Project[],
        siehtRechnungen ? Promise.resolve().then(() => abrechnungService.posten({ companyId: id })).catch(() => [] as Abrechnungsposten[]) : [] as Abrechnungsposten[],
        siehtOffenePosten ? Promise.resolve().then(() => rechnungService.offenePostenFirma(id)).catch(() => null) : null,
        // Vertraege lesen mit vertrieb.lesen -- wie die Akte selbst.
        Promise.resolve().then(() => vertragService.list({ companyId: id })).catch(() => [] as Vertrag[]),
      ]);
      setAkte(a ?? null);
      setAngebote(Array.isArray(q) ? q : []);
      setAufgaben(Array.isArray(t) ? t : []);
      setAuftraege(Array.isArray(ao) ? ao : []);
      setRechnungen(Array.isArray(re) ? re : []);
      setProjekte(Array.isArray(pr) ? pr : []);
      setAbrechnungsposten(Array.isArray(ap) ? ap : []);
      setOffenePosten(op ?? null);
      setVertraege(Array.isArray(vt) ? vt : []);
      setFehler(null);
    } catch (e) {
      setFehler(fehlerText(e, 'Akte konnte nicht geladen werden'));
    } finally {
      setLaedt(false);
    }
  }, [id, siehtAngebote, siehtAuftraege, siehtRechnungen, siehtOffenePosten, siehtProjekte]);

  useEffect(() => { laden(); }, [laden]);

  const melde = (e: unknown, standard: string) => setFehler(fehlerText(e, standard));

  const feldSpeichern = async (feld: string, wert: any) => {
    if (!id) return;
    try {
      await companyService.update(id, { [feld]: wert } as any);
      await laden();
    } catch (e) { melde(e, 'Speichern fehlgeschlagen'); }
  };

  const notizSpeichern = async () => {
    if (!id || !notiz.trim()) return;
    try {
      await companyService.addNote(id, notiz.trim(), notizArt);
      setNotiz('');
      await laden();
    } catch (e) { melde(e, 'Notiz konnte nicht gespeichert werden'); }
  };

  const kontaktAnlegen = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!id || !neuKontakt.email.trim()) return;
    try {
      await companyService.addContact(id, {
        email: neuKontakt.email.trim().toLowerCase(),
        firstName: neuKontakt.firstName.trim() || undefined,
        lastName: neuKontakt.lastName.trim() || undefined,
        jobTitle: neuKontakt.jobTitle.trim() || undefined,
        phone: neuKontakt.phone.trim() || undefined,
        linkedInUrl: neuKontakt.linkedInUrl.trim() || undefined,
        isPrimary: neuKontakt.isPrimary || undefined,
      });
      setNeuKontakt(LEER_KONTAKT);
      setZeigeKontaktform(false);
      push({ message: 'Ansprechpartner angelegt.', severity: 'success' });
      await laden();
    } catch (e) { melde(e, 'Ansprechpartner konnte nicht angelegt werden'); }
  };

  const hauptkontakt = async (leadId: string) => {
    if (!id) return;
    try {
      await companyService.assignContact(id, leadId, true);
      await laden();
    } catch (e) { melde(e, 'Änderung fehlgeschlagen'); }
  };

  const kontaktAendern = async (leadId: string, daten: { kontaktRollen?: KontaktRolle[]; advisoryOptOut?: boolean }) => {
    try {
      await companyService.updateContact(leadId, daten);
      await laden();
    } catch (e) { melde(e, 'Änderung fehlgeschlagen'); }
  };

  const rolleUmschalten = (kontakt: CompanyContact, rolle: KontaktRolle) => {
    const jetzt = kontakt.kontaktRollen ?? [];
    const neu = jetzt.includes(rolle) ? jetzt.filter((r) => r !== rolle) : [...jetzt, rolle];
    return kontaktAendern(kontakt.id, { kontaktRollen: neu });
  };

  const kontaktLoesen = async (leadId: string, name: string) => {
    const ok = await confirm({
      title: 'Ansprechpartner lösen?',
      message: `${name} bleibt als Lead erhalten, gehört aber nicht mehr zu dieser Firma.`,
      confirmLabel: 'Lösen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await companyService.removeContact(leadId);
      await laden();
    } catch (e) { melde(e, 'Lösen fehlgeschlagen'); }
  };

  const vorgangAnlegen = async () => {
    if (!akte || !id) return;
    const titel = await prompt({
      title: 'Neuer Vorgang',
      label: 'Titel',
      defaultValue: `${akte.firma.name}: `,
      required: true,
      confirmLabel: 'Anlegen',
    });
    if (!titel) return;
    setBusy(true);
    try {
      const deal = await crmService.createDeal({ title: titel.trim(), company: akte.firma.name, companyId: id, value: 0 });
      push({ message: 'Vorgang in der Pipeline angelegt.', severity: 'success' });
      navigate(`/pipeline?deal=${deal.id}`);
    } catch (e) { melde(e, 'Vorgang konnte nicht angelegt werden'); } finally { setBusy(false); }
  };

  const angebotAnlegen = async () => {
    if (!akte || !id) return;
    setBusy(true);
    try {
      const q = await quoteService.create({ companyId: id, title: `Angebot für ${akte.firma.name}` });
      push({ message: `Angebot ${q.number} angelegt.`, severity: 'success' });
      navigate(`/angebote?angebot=${q.id}`);
    } catch (e) { melde(e, 'Angebot konnte nicht angelegt werden'); } finally { setBusy(false); }
  };

  const aufgabeAnlegen = async () => {
    if (!id) return;
    const titel = await prompt({
      title: 'Aufgabe zu dieser Firma',
      label: 'Was ist zu tun?',
      placeholder: 'z. B. Rückruf wegen Angebot',
      required: true,
      confirmLabel: 'Anlegen',
    });
    if (!titel) return;
    try {
      await taskService.create({ title: titel.trim(), companyId: id, dueAt: new Date().toISOString() });
      push({ message: 'Aufgabe für heute angelegt.', severity: 'success' });
      await laden();
    } catch (e) { melde(e, 'Aufgabe konnte nicht angelegt werden'); }
  };

  const aufgabeErledigt = async (t: Task) => {
    try {
      await taskService.erledigen(t.id, true);
      await laden();
    } catch (e) { melde(e, 'Abhaken fehlgeschlagen'); }
  };

  if (laedt && !akte) {
    return (
      <div className="page-container-wide">
        <SalesProcessBar label="Firmenakte" />
        <LoadingState rows={6} label="Akte wird geladen" />
      </div>
    );
  }

  if (!akte) {
    return (
      <div className="page-container-wide">
        <SalesProcessBar label="Firmenakte" />
        <EmptyState
          icon="alert"
          title="Akte nicht gefunden"
          description={fehler ?? 'Diese Firma gibt es nicht (mehr).'}
          action={{ label: 'Zur Firmenliste', to: '/companies' }}
        />
      </div>
    );
  }

  const { firma, kennzahlen: k, chronik } = akte;
  const gefiltert = filter ? chronik.filter((e) => e.art === filter) : chronik;
  const kontaktName = (c: { firstName?: string; lastName?: string; email: string }) =>
    [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email;

  return (
    <div className="page-container-wide">
      <SalesProcessBar label="Firmenakte" />
      <PageHeader
        eyebrow={<Link to="/companies" className="akte-zurueck"><Icon name="arrow-left" size={12} /> Firmen</Link>}
        title={firma.name}
        subtitle={(
          <>
            {firma.domain && <a href={`https://${firma.domain}`} target="_blank" rel="noreferrer">{firma.domain}</a>}
            {firma.industry && <> · {firma.industry}</>}
            {(firma.postalCode || firma.city) && <> · {[firma.postalCode, firma.city].filter(Boolean).join(' ')}</>}
            {firma.phone && <> · <a href={`tel:${firma.phone}`}>{firma.phone}</a></>}
          </>
        )}
        actions={(
          <>
            <label className="akte-status">
              <span className="sr-only">Status</span>
              <select className="form-select" value={firma.status} disabled={!darfBearbeiten} onChange={(e) => feldSpeichern('status', e.target.value)} aria-label="Status der Firma">
                {FIRMEN_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
            <label className="akte-kunde" title="Bestandskunden erhalten Sicherheitsinformationen bei kritischen Schwachstellen (über SecTepe.Core). Mit Kundennummer gilt die Firma immer als Kunde.">
              <input
                type="checkbox"
                checked={Boolean(firma.istKunde || firma.kundennummer)}
                disabled={!darfBearbeiten || Boolean(firma.kundennummer)}
                onChange={(e) => feldSpeichern('istKunde', e.target.checked)}
              />{' '}
              Kunde
            </label>
            {darfBearbeiten && <button type="button" className="btn btn-secondary" onClick={aufgabeAnlegen}><Icon name="check" size={14} /> Aufgabe</button>}
            {darfAngebot && <button type="button" className="btn btn-secondary" onClick={angebotAnlegen} disabled={busy}><Icon name="file-check" size={14} /> Angebot</button>}
            {darfBearbeiten && <button type="button" className="btn btn-primary" onClick={vorgangAnlegen} disabled={busy}><Icon name="kanban" size={14} /> Vorgang</button>}
          </>
        )}
      />
      <RechteHinweis was="Firmenakte bearbeiten, Vorgänge, Aufgaben und Notizen anlegen" />

      {fehler && <div className="alert alert-error">{fehler}</div>}

      <KpiRow
        aria-label="Kennzahlen der Firma"
        items={[
          { label: 'Ansprechpartner', value: k.kontakte },
          { label: 'Antworten', value: k.antworten },
          { label: 'Interesse', value: k.interesse, tone: 'good' },
          { label: 'Absagen', value: k.absagen },
          { label: 'Abmeldungen', value: k.abmeldungen, tone: k.abmeldungen ? 'warn' : 'neutral' },
          { label: 'Unzustellbar', value: k.unzustellbar, tone: k.unzustellbar ? 'warn' : 'neutral' },
          { label: 'Vorgänge offen', value: k.vorgaengeOffen, to: '/pipeline', hint: k.wertOffen ? euro(k.wertOffen) : undefined },
          { label: 'gewonnen', value: euro(k.wertGewonnen), tone: 'good', hint: `${k.vorgaengeGewonnen} Vorgänge` },
        ]}
      />

      {k.letzteBeruehrung?.titel && (
        <p className="akte-letzte">
          Zuletzt: <strong>{k.letzteBeruehrung.titel}</strong> am {zeit(k.letzteBeruehrung.zeitpunkt)}
          {(k.letzteBeruehrung.kampagneId || k.letzteBeruehrung.kampagne) && (
            <> aus <KampagneVerweis lemlistId={k.letzteBeruehrung.kampagneId} name={k.letzteBeruehrung.kampagne} zuordnung={zuordnung} /></>
          )}
        </p>
      )}

      <div className="akte-spalten">
        <div className="akte-links">
          <section className="akte-block" aria-label="Ansprechpartner">
            <div className="akte-block-kopf">
              <h2>Ansprechpartner ({firma.contacts?.length ?? 0})</h2>
              {darfBearbeiten && (
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => { setNeuKontakt(LEER_KONTAKT); setZeigeKontaktform(true); }}>
                  <Icon name="plus" size={12} /> Hinzufügen
                </button>
              )}
            </div>

            <Modal open={zeigeKontaktform && darfBearbeiten} onOpenChange={(o) => { if (!o) setZeigeKontaktform(false); }}>
              <ModalContent>
                <form onSubmit={kontaktAnlegen} aria-label="Neuer Ansprechpartner">
                  <ModalHeader>
                    <ModalTitle>Ansprechpartner bei {firma.name}</ModalTitle>
                    <ModalDescription>Wird als Lead angelegt und der Firma zugeordnet. Nur die E-Mail-Adresse ist Pflicht.</ModalDescription>
                  </ModalHeader>
                  <ModalBody>
                    <div className="akte-felder">
                      <label>E-Mail *<input type="email" required autoFocus placeholder="name@firma.de" value={neuKontakt.email} onChange={(e) => setNeuKontakt({ ...neuKontakt, email: e.target.value })} /></label>
                      <label>Funktion<input placeholder="CISO" value={neuKontakt.jobTitle} onChange={(e) => setNeuKontakt({ ...neuKontakt, jobTitle: e.target.value })} /></label>
                      <label>Vorname<input value={neuKontakt.firstName} onChange={(e) => setNeuKontakt({ ...neuKontakt, firstName: e.target.value })} /></label>
                      <label>Nachname<input value={neuKontakt.lastName} onChange={(e) => setNeuKontakt({ ...neuKontakt, lastName: e.target.value })} /></label>
                      <label>Telefon<input type="tel" value={neuKontakt.phone} onChange={(e) => setNeuKontakt({ ...neuKontakt, phone: e.target.value })} /></label>
                      <label>LinkedIn<input type="url" placeholder="https://linkedin.com/in/…" value={neuKontakt.linkedInUrl} onChange={(e) => setNeuKontakt({ ...neuKontakt, linkedInUrl: e.target.value })} /></label>
                    </div>
                    <label className="akte-checkbox">
                      <input type="checkbox" checked={neuKontakt.isPrimary} onChange={(e) => setNeuKontakt({ ...neuKontakt, isPrimary: e.target.checked })} />
                      {' '}Als Hauptansprechpartner führen
                    </label>
                  </ModalBody>
                  <ModalFooter>
                    <button type="button" className="btn btn-ghost" onClick={() => setZeigeKontaktform(false)}>Abbrechen</button>
                    <button type="submit" className="btn btn-primary" disabled={!neuKontakt.email.trim()}>Anlegen</button>
                  </ModalFooter>
                </form>
              </ModalContent>
            </Modal>

            {(firma.contacts || []).length === 0 ? (
              <p className="inbox-item-hinweis">
                Noch keine Ansprechpartner.
                {/* „Leads zuordnen“ (POST /companies/backfill) gibt es nur mit vertrieb.loeschen. */}
                {darfLoesen && ' „Leads zuordnen“ in der Firmenliste hängt vorhandene Kontakte automatisch ein.'}
              </p>
            ) : (
              <ul className="kontakt-liste">
                {(firma.contacts || []).map((kontakt) => (
                  <li key={kontakt.id}>
                    <div className="kontakt-zeile">
                      <div>
                        {kontakt.isPrimary && <StatusBadge tone="primary">Haupt</StatusBadge>}{' '}
                        <Link to={`/lead-sync?lead=${encodeURIComponent(kontakt.id)}`} title="In Leads & Outreach öffnen"><strong>{kontaktName(kontakt)}</strong></Link>
                        {kontakt.jobTitle && <span className="kontakt-rolle"> · {kontakt.jobTitle}</span>}
                      </div>
                      <div className="kontakt-aktionen">
                        {darfBearbeiten && !kontakt.isPrimary && (
                          <button type="button" className="btn btn-sm btn-ghost" onClick={() => hauptkontakt(kontakt.id)}>zum Haupt</button>
                        )}
                        {darfLoesen && (
                          <button type="button" className="btn btn-sm btn-ghost" onClick={() => kontaktLoesen(kontakt.id, kontaktName(kontakt))}>lösen</button>
                        )}
                      </div>
                    </div>
                    <div className="kontakt-detail">
                      <a href={`mailto:${kontakt.email}`}>{kontakt.email}</a>
                      {kontakt.phone && <> · <a href={`tel:${kontakt.phone}`}>{kontakt.phone}</a></>}
                      {kontakt.linkedInUrl && <> · <a href={kontakt.linkedInUrl} target="_blank" rel="noreferrer">LinkedIn</a></>}
                      {kontakt.lemlistStatus && <> · <StatusBadge status={kontakt.lemlistStatus}>{kontakt.lemlistStatus}</StatusBadge></>}
                      {kontakt.lemlistCampaignId && <> · <KampagneVerweis lemlistId={kontakt.lemlistCampaignId} zuordnung={zuordnung} /></>}
                    </div>
                    {(firma.istKunde || firma.kundennummer) && (
                      <div className="kontakt-rollen" aria-label={`Rollen von ${kontaktName(kontakt)}`}>
                        {KONTAKT_ROLLEN.map((r) => {
                          const aktiv = (kontakt.kontaktRollen ?? []).includes(r.value);
                          return (
                            <button
                              key={r.value}
                              type="button"
                              className={`btn btn-sm ${aktiv ? 'btn-secondary' : 'btn-ghost'}`}
                              aria-pressed={aktiv}
                              disabled={!darfBearbeiten}
                              onClick={() => rolleUmschalten(kontakt, r.value)}
                            >
                              {r.label}
                            </button>
                          );
                        })}
                        {kontakt.advisoryOptOut ? (
                          <>
                            {' '}<StatusBadge tone="warning">von Sicherheitsinfos abgemeldet</StatusBadge>
                            {darfBearbeiten && (
                              <button type="button" className="btn btn-sm btn-ghost" onClick={() => kontaktAendern(kontakt.id, { advisoryOptOut: false })}>wieder anmelden</button>
                            )}
                          </>
                        ) : darfBearbeiten && (
                          <button type="button" className="btn btn-sm btn-ghost" onClick={() => kontaktAendern(kontakt.id, { advisoryOptOut: true })}>Sicherheitsinfos abbestellen</button>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="akte-block" aria-label="Vorgänge">
            <div className="akte-block-kopf">
              <h2>Vorgänge ({firma.deals?.length ?? 0})</h2>
              {darfBearbeiten && <button type="button" className="btn btn-sm btn-secondary" onClick={vorgangAnlegen} disabled={busy}>+ Vorgang</button>}
            </div>
            {(firma.deals || []).length === 0 ? (
              <p className="inbox-item-hinweis">Noch kein Vorgang. Ein Vorgang ist ein Geschäft in der Pipeline – mit Stufe, Wert und nächstem Schritt.</p>
            ) : (
              <ul className="kontakt-liste">
                {(firma.deals || []).map((d) => {
                  const st = DEAL_STATUS[d.status] ?? { label: d.status, tone: 'info' as const };
                  return (
                    <li key={d.id}>
                      <div className="kontakt-zeile">
                        <Link to={`/pipeline?deal=${d.id}`}><strong>{d.title}</strong></Link>
                        <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
                      </div>
                      <div className="kontakt-detail">
                        {euro(d.value, d.currency)}{d.stage?.name && <> · {d.stage.name}</>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {siehtAngebote && (
            <section className="akte-block" aria-label="Angebote">
              <div className="akte-block-kopf">
                <h2>Angebote ({angebote.length})</h2>
                {darfAngebot && <button type="button" className="btn btn-sm btn-secondary" onClick={angebotAnlegen} disabled={busy}>+ Angebot</button>}
              </div>
              {angebote.length === 0 ? (
                <p className="inbox-item-hinweis">Noch kein Angebot für diese Firma.</p>
              ) : (
                <ul className="kontakt-liste">
                  {angebote.map((q) => (
                    <li key={q.id}>
                      <div className="kontakt-zeile">
                        <Link to={`/angebote?angebot=${q.id}`}><strong>{q.number}</strong>{q.title && <> · {q.title}</>}</Link>
                        <StatusBadge status={q.status}>{ANGEBOT_STATUS[q.status] || q.status}</StatusBadge>
                      </div>
                      <div className="kontakt-detail">{euroCents(q.netCents, q.currency)} netto</div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          <section className="akte-block" aria-label="Verträge">
            <div className="akte-block-kopf">
              <h2>Verträge ({vertraege.length})</h2>
              {darfAngebot && <Link to={`/vertraege?neu=1&firma=${id}`} className="btn btn-sm btn-secondary">+ Vertrag</Link>}
            </div>
            {vertraege.length === 0 ? (
              <p className="inbox-item-hinweis">Noch kein Vertrag, keine AVV und kein NDA hinterlegt.</p>
            ) : (
              <ul className="kontakt-liste">
                {vertraege.map((v) => (
                  <li key={v.id}>
                    <div className="kontakt-zeile">
                      <Link to={`/vertraege?vertrag=${v.id}`}><strong>{v.number || '—'}</strong> · {v.titel}</Link>
                      <StatusBadge tone={VERTRAG_STATUS_TONE[v.status] || 'neutral'}>{VERTRAG_STATUS_LABEL[v.status] || v.status}</StatusBadge>
                    </div>
                    <div className="kontakt-detail">
                      {VERTRAG_ART_LABEL[v.art] || v.art}{v.hauptvertrag && <> · Anlage zu {v.hauptvertrag.number || v.hauptvertrag.titel}</>}{' '}
                      <VertragFristBadge fristen={v.fristen} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {siehtAuftraege && (
            <section className="akte-block" aria-label="Aufträge">
              <div className="akte-block-kopf">
                <h2>Aufträge ({auftraege.length})</h2>
                <Link to="/auftraege" className="btn btn-sm btn-secondary">Alle Aufträge</Link>
              </div>
              {auftraege.length === 0 ? (
                <p className="inbox-item-hinweis">Noch kein Auftrag. Aus einem angenommenen Angebot entsteht er per Klick.</p>
              ) : (
                <ul className="kontakt-liste">
                  {auftraege.map((a) => (
                    <li key={a.id}>
                      <div className="kontakt-zeile">
                        <Link to={`/auftraege?auftrag=${a.id}`}><strong>{a.number || 'Entwurf'}</strong>{a.title && <> · {a.title}</>}</Link>
                        <StatusBadge status={a.status}>{AUFTRAG_STATUS_LABEL[a.status] || a.status}</StatusBadge>
                      </div>
                      <div className="kontakt-detail">{euroCents(a.netCents, a.currency)} netto</div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {siehtRechnungen && (
            <>
              <section className="akte-block" aria-label="Rechnungen">
                <div className="akte-block-kopf">
                  <h2>Rechnungen ({rechnungen.length})</h2>
                  <Link to="/rechnungen" className="btn btn-sm btn-secondary">Alle Rechnungen</Link>
                </div>
                {rechnungen.length === 0 ? (
                  <p className="inbox-item-hinweis">Noch keine Rechnung für diese Firma.</p>
                ) : (
                  <ul className="kontakt-liste">
                    {rechnungen.map((r) => {
                      const offenCents = r.status === 'draft' || r.status === 'cancelled' ? 0 : r.grossCents - (r.bezahltCents || 0);
                      return (
                        <li key={r.id}>
                          <div className="kontakt-zeile">
                            <Link to={`/rechnungen?rechnung=${r.id}`}><strong>{r.number || 'Entwurf'}</strong></Link>
                            <StatusBadge status={r.status}>{RECHNUNG_STATUS_LABEL[r.status] || r.status}</StatusBadge>
                          </div>
                          <div className="kontakt-detail">
                            {euroCents(r.grossCents, r.currency)} brutto{offenCents > 0 && <> · <span className={r.status === 'overdue' ? 'text-error' : ''}>{euroCents(offenCents, r.currency)} offen</span></>}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              <section className="akte-block" aria-label="Abrechnungsposten">
                <div className="akte-block-kopf">
                  <h2>Abrechnungsposten ({abrechnungsposten.length})</h2>
                  <Link to="/tagesabrechnung" className="btn btn-sm btn-secondary">Tagesabrechnung</Link>
                </div>
                {abrechnungsposten.length === 0 ? (
                  <p className="inbox-item-hinweis">Keine Posten aus den Produkten für diese Firma.</p>
                ) : (
                  <ul className="kontakt-liste">
                    {abrechnungsposten.slice(0, 20).map((p) => (
                      <li key={p.id}>
                        <div className="kontakt-zeile">
                          <strong>{QUELLE_LABEL[p.quelle] ?? p.quelle}</strong> · {p.vorgangRef}
                          <StatusBadge tone={p.status === 'fehler' ? 'danger' : p.status === 'abgerechnet' ? 'success' : p.status === 'storniert' ? 'neutral' : 'warning'}>{POSTEN_STATUS_LABEL[p.status] ?? p.status}</StatusBadge>
                        </div>
                        <div className="kontakt-detail">
                          {p.description} · {euroCents(Math.round((p.quantityHundert / 100) * p.unitPriceCents), p.currency)} netto
                          {p.invoiceId && <> · <Link to={`/rechnungen?rechnung=${p.invoiceId}`}>Rechnung</Link></>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}

          {siehtOffenePosten && (
            <section className="akte-block" aria-label="Offene Posten">
              <div className="akte-block-kopf">
                <h2>Offene Posten</h2>
                {darfZahlungen && <Link to="/zahlungen" className="btn btn-sm btn-secondary">Zahlungen</Link>}
              </div>
              {!offenePosten ? (
                <p className="inbox-item-hinweis">Offene Posten konnten nicht geladen werden.</p>
              ) : offenePosten.anzahl === 0 ? (
                <p className="inbox-item-hinweis">Nichts offen.</p>
              ) : (
                <p className="inbox-item-hinweis">
                  <strong>{euroCents(offenePosten.offenCents)}</strong> offen aus {offenePosten.anzahl} Rechnung(en)
                  {offenePosten.ueberfaelligCents > 0 && <> · <span className="text-error">{euroCents(offenePosten.ueberfaelligCents)} überfällig</span></>}
                  {offenePosten.aeltesteFaelligkeit && <> · älteste Fälligkeit {new Date(offenePosten.aeltesteFaelligkeit).toLocaleDateString('de-DE')}</>}
                </p>
              )}
            </section>
          )}

          {siehtProjekte && (
            <section className="akte-block" aria-label="Projekte">
              <div className="akte-block-kopf">
                <h2>Projekte ({projekte.length})</h2>
                <Link to="/projekte" className="btn btn-sm btn-secondary">Alle Projekte</Link>
              </div>
              {projekte.length === 0 ? (
                <p className="inbox-item-hinweis">Noch kein Projekt für diese Firma.</p>
              ) : (
                <ul className="kontakt-liste">
                  {projekte.map((p) => (
                    <li key={p.id}>
                      <div className="kontakt-zeile">
                        <Link to={`/projekte?projekt=${p.id}`}><strong>{p.number ? `${p.number} · ` : ''}{p.name}</strong></Link>
                        <StatusBadge status={p.status}>{PROJEKT_STATUS_LABEL[p.status] || p.status}</StatusBadge>
                      </div>
                      {p.budgetCents > 0 && <div className="kontakt-detail">Budget {euroCents(p.budgetCents)}</div>}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          <section className="akte-block" aria-label="Offene Aufgaben">
            <div className="akte-block-kopf">
              <h2>Offene Aufgaben ({aufgaben.length})</h2>
              {darfBearbeiten && <button type="button" className="btn btn-sm btn-secondary" onClick={aufgabeAnlegen}>+ Aufgabe</button>}
            </div>
            {aufgaben.length === 0 ? (
              <p className="inbox-item-hinweis">Nichts offen. <Link to="/aufgaben">Alle Aufgaben</Link></p>
            ) : (
              <ul className="aufgaben-liste">
                {aufgaben.map((t) => {
                  const f = faelligkeit(t.dueAt);
                  return (
                    <li key={t.id} className={`aufgabe ${f?.ueberfaellig ? 'aufgabe-ueberfaellig' : ''}`}>
                      <input type="checkbox" aria-label={`${t.title} erledigt`} disabled={!darfBearbeiten} onChange={() => aufgabeErledigt(t)} />
                      <div className="aufgabe-inhalt">
                        <div className="aufgabe-kopf">
                          <Link to={`/aufgaben?aufgabe=${encodeURIComponent(t.id)}`}><strong>{t.title}</strong></Link>
                          {f && <span className={`aufgabe-termin${f.ueberfaellig ? ' text-error' : ''}`}>{f.text}</span>}
                        </div>
                        {t.deal && <div className="aufgabe-bezug"><Link to={`/pipeline?deal=${t.deal.id}`}>{t.deal.title}</Link></div>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="akte-block" aria-label="Stammdaten">
            <h2>Stammdaten</h2>
            <div className="akte-felder">
              {([
                ['website', 'Website'], ['industry', 'Branche'], ['size', 'Größe'],
                ['address', 'Adresse'], ['postalCode', 'PLZ'], ['city', 'Ort'],
                ['region', 'Region'], ['country', 'Land'], ['phone', 'Telefon'],
                ['linkedInUrl', 'LinkedIn'],
              ] as [string, string][]).map(([feld, label]) => (
                <label key={feld}>
                  {label}
                  <input
                    defaultValue={(firma as any)[feld] || ''}
                    disabled={!darfBearbeiten}
                    onBlur={(e) => {
                      if (e.target.value !== ((firma as any)[feld] || '')) feldSpeichern(feld, e.target.value);
                    }}
                  />
                </label>
              ))}
            </div>
            {darfBearbeiten && <p className="form-helper">Änderungen werden beim Verlassen des Feldes gespeichert.</p>}
          </section>
        </div>

        <div className="akte-rechts">
          <AccountProfil companyId={firma.id} />

          <section className="akte-block" aria-label="Chronik">
            <div className="akte-block-kopf">
              <h2>Chronik</h2>
              <select className="form-select select-auto" value={filter} onChange={(e) => setFilter(e.target.value as EintragArt | '')} aria-label="Chronik filtern">
                <option value="">alles</option>
                <option value="aktivitaet">nur Reaktionen</option>
                <option value="notiz">nur Notizen</option>
                <option value="vorgang">nur Vorgänge</option>
                <option value="kontakt">nur Kontakte</option>
              </select>
            </div>

            {darfBearbeiten && (
              <div className="notiz-eingabe">
                <select className="form-select select-auto notiz-art" value={notizArt} onChange={(e) => setNotizArt(e.target.value)} aria-label="Art des Eintrags">
                  <option value="note">Notiz</option>
                  <option value="call">Telefonat</option>
                  <option value="meeting">Termin</option>
                  <option value="research">Recherche</option>
                </select>
                <textarea
                  className="form-textarea"
                  rows={3}
                  placeholder="Was ist passiert?"
                  value={notiz}
                  onChange={(e) => setNotiz(e.target.value)}
                />
                <button type="button" className="btn btn-primary" onClick={notizSpeichern} disabled={!notiz.trim()}>
                  Eintragen
                </button>
              </div>
            )}

            {gefiltert.length === 0 && (
              <EmptyState
                compact
                icon="clock"
                title="Noch nichts vermerkt"
                description="Reaktionen aus den Kampagnen erscheinen hier nach dem nächsten Abgleich unter „Antworten“."
                action={{ label: 'Zu den Antworten', to: '/outreach-inbox' }}
              />
            )}

            {nachTagen(gefiltert).map((gruppe) => (
              <div key={gruppe.tag} className="chronik-tag">
                <h4>{tagLang(gruppe.tag)}</h4>
                <ul className="chronik">
                  {gruppe.eintraege.map((e) => (
                    <li key={e.id} className={`chronik-${e.art}`}>
                      <div className="chronik-kopf">
                        <span className={`badge badge-${e.art}`}>{ART_LABEL[e.art]}</span>
                        {e.dealId ? <Link to={`/pipeline?deal=${e.dealId}`}><strong>{e.titel}</strong></Link> : <strong>{e.titel}</strong>}
                        {e.kanal && e.kanal !== 'sonstige' && <span className="badge badge-neutral">{e.kanal}</span>}
                        {e.handled === false && (
                          <Link
                            to={e.id.startsWith('act-') ? `/outreach-inbox?eintrag=${encodeURIComponent(e.id.slice(4))}` : '/outreach-inbox'}
                            className="badge badge-warn"
                            title="Antwort im Posteingang öffnen"
                          >
                            offen
                          </Link>
                        )}
                        <span className="chronik-zeit">{zeit(e.zeitpunkt)}</span>
                      </div>
                      {e.text && <div className="chronik-text">{e.text}</div>}
                      <div className="chronik-fuss">
                        {e.autor && <>von {e.autor}</>}
                        {(e.lemlistCampaignId || e.quelle) && (
                          <> · {e.lemlistCampaignId
                            ? <KampagneVerweis lemlistId={e.lemlistCampaignId} name={e.quelle} zuordnung={zuordnung} />
                            : e.quelle}</>
                        )}
                        {e.leadEmail && <> · {e.leadId
                          ? <Link to={`/lead-sync?lead=${encodeURIComponent(e.leadId)}`} title="Lead öffnen">{e.leadEmail}</Link>
                          : e.leadEmail}</>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        </div>
      </div>
    </div>
  );
};

export default CompanyAktePage;
